/**
 * Dashboard画面用のSupabaseデータ取得カスタムフック
 * Issue #237 Phase 2: フロントエンド統合・リアルタイム機能実装
 */

import { useCallback } from 'react';
import logger from '../utils/logger';

import { useState, useEffect } from 'react';
import { DataService } from '../services/dataService';
import type { ExtendedCustomer } from '../types/salonboard';
import type { Reservation } from '../types/reservation';
import type { TreatmentRecord } from '../types/treatment';

export interface DashboardMetrics {
  monthlySales: number;
  monthlyCustomers: number;
  averageSpending: number;
  repeatRate: number;
  todayReservations: number;
  todayRevenue: number;
}

export interface ChartDataPoint {
  date: string;
  visitors: number;         // 来店数
  revenue: number;          // 売上
  newCustomers: number;     // 新規来店数
  repeatCustomers: number;  // リピーター来店数
}

export interface DashboardData {
  metrics: DashboardMetrics;
  todaysReservations: Reservation[];
  frequentCustomers: ExtendedCustomer[];
  recentActivities: Activity[];
  availableSlots: number;
  chartData: ChartDataPoint[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
}

export interface Activity {
  id: string;
  type: 'reservation' | 'customer' | 'sales' | 'staff' | 'alert';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timestamp: Date;
  relatedId?: string;
  actionRequired?: boolean;
}

/**
 * Dashboard画面のデータ取得・管理カスタムフック
 * @param selectedDate - 選択された日付（省略時は今日）
 */
export const useDashboardData = (selectedDate?: Date): DashboardData => {
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    monthlySales: 0,
    monthlyCustomers: 0,
    averageSpending: 0,
    repeatRate: 0,
    todayReservations: 0,
    todayRevenue: 0,
  });

  const [todaysReservations, setTodaysReservations] = useState<Reservation[]>([]);
  const [frequentCustomers, setFrequentCustomers] = useState<ExtendedCustomer[]>([]);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [availableSlots, setAvailableSlots] = useState(0);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 安全な今日の日付取得（タイムゾーン考慮）
  const getSafeToday = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 安全な日付比較関数
  const safeDateComparison = (reservationDate: string | Date, targetDate: string): boolean => {
    try {
      let dateStr: string;
      if (reservationDate instanceof Date) {
        const year = reservationDate.getFullYear();
        const month = String(reservationDate.getMonth() + 1).padStart(2, '0');
        const day = String(reservationDate.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
      } else {
        dateStr = String(reservationDate).split('T')[0];
      }
      return dateStr === targetDate;
    } catch (error) {
      logger.warn('🚨 Date comparison error:', error, 'reservationDate:', reservationDate);
      return false;
    }
  };

  // 選択された日付を文字列形式に変換（未指定時は今日）
  const targetDateStr = selectedDate
    ? (() => {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(selectedDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      })()
    : getSafeToday();

  const today = targetDateStr;

  /**
   * 月次メトリクスを計算
   */
  const calculateMonthlyMetrics = async (
    customers: ExtendedCustomer[],
    reservations: Reservation[],
    treatments: TreatmentRecord[]
  ): Promise<DashboardMetrics> => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // 今月のデータをフィルター
    const thisMonthReservations = reservations.filter(reservation => {
      const reservationDate = new Date(reservation.date);
      return reservationDate.getMonth() === currentMonth && 
             reservationDate.getFullYear() === currentYear &&
             reservation.status === 'completed';
    });

    const thisMonthTreatments = treatments.filter(treatment => {
      const treatmentDate = new Date(treatment.date);
      return treatmentDate.getMonth() === currentMonth && 
             treatmentDate.getFullYear() === currentYear;
    });

    // 今日のデータをフィルター（安全な日付比較使用）
    const todayReservations = reservations.filter(reservation => {
      return safeDateComparison(reservation.date, today) && reservation.status !== 'cancelled';
    });

    const todayTreatments = treatments.filter(treatment => {
      return safeDateComparison(treatment.date, today);
    });

    // メトリクス計算
    const monthlySales = thisMonthTreatments.reduce((sum, treatment) => 
      sum + (treatment.totalAmount || 0), 0
    );
    
    const monthlyCustomerIds = new Set(thisMonthReservations.map(r => r.customerId));
    const monthlyCustomers = monthlyCustomerIds.size;
    
    const averageSpending = monthlyCustomers > 0 ? monthlySales / monthlyCustomers : 0;
    
    // リピート率計算（過去6ヶ月のデータを使用）
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    const recentCustomers = customers.filter(customer => {
      const lastVisit = new Date(customer.lastVisit);
      return lastVisit >= sixMonthsAgo;
    });
    
    const repeatCustomers = recentCustomers.filter(customer => customer.totalVisits > 1);
    const repeatRate = recentCustomers.length > 0 ? 
      Math.round((repeatCustomers.length / recentCustomers.length) * 100) : 0;

    const todayRevenue = todayTreatments.reduce((sum, treatment) => 
      sum + treatment.totalAmount, 0
    );

    return {
      monthlySales,
      monthlyCustomers,
      averageSpending,
      repeatRate,
      todayReservations: todayReservations.length,
      todayRevenue,
    };
  };

  /**
   * 頻繁な顧客を取得（月10回以上利用）
   */
  const getFrequentCustomers = (customers: ExtendedCustomer[]): ExtendedCustomer[] => {
    return customers
      .filter(customer => customer.totalVisits >= 10)
      .sort((a, b) => b.totalVisits - a.totalVisits)
      .slice(0, 5);
  };

  /**
   * 最近の活動を生成
   */
  const generateRecentActivities = (
    reservations: Reservation[],
    customers: ExtendedCustomer[],
    treatments: TreatmentRecord[]
  ): Activity[] => {
    const activities: Activity[] = [];

    // 今日の予約活動（安全な日付比較使用）
    const todayReservations = reservations.filter(r => {
      return safeDateComparison(r.date, today) && r.status !== 'cancelled';
    });

    todayReservations.forEach(reservation => {
      activities.push({
        id: `res-${reservation.id}`,
        type: 'reservation',
        priority: 'medium',
        title: `予約: ${reservation.customerName}様`,
        description: `${reservation.startTime} - ${reservation.serviceName}`,
        timestamp: new Date(reservation.date + 'T' + reservation.startTime),
        relatedId: reservation.id,
        actionRequired: reservation.status === 'confirmed',
      });
    });

    // キャンセルされた予約（安全な日付比較使用）
    const cancelledReservations = reservations.filter(r => {
      return safeDateComparison(r.date, today) && r.status === 'cancelled';
    });

    cancelledReservations.forEach(reservation => {
      activities.push({
        id: `cancel-${reservation.id}`,
        type: 'alert',
        priority: 'high',
        title: `キャンセル: ${reservation.customerName}様`,
        description: `${reservation.startTime} - ${reservation.serviceName}`,
        timestamp: new Date(reservation.updatedAt || reservation.createdAt || Date.now()),
        relatedId: reservation.id,
        actionRequired: true,
      });
    });

    // 新規顧客
    const newCustomers = customers.filter(customer => {
      const createdDate = new Date(customer.createdAt);
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      return createdDate >= threeDaysAgo;
    });

    newCustomers.forEach(customer => {
      activities.push({
        id: `new-customer-${customer.id}`,
        type: 'customer',
        priority: 'low',
        title: `新規顧客: ${customer.name}様`,
        description: `初回来店予定: ${customer.lastVisit}`,
        timestamp: new Date(customer.createdAt),
        relatedId: customer.id.toString(),
        actionRequired: false,
      });
    });

    // 今日の売上が目標を超えた場合（安全な日付比較使用）
    const todayRevenue = treatments
      .filter(t => safeDateComparison(t.date, today))
      .reduce((sum, treatment) => sum + (treatment.totalAmount || 0), 0);
    
    if (todayRevenue > 50000) {
      activities.push({
        id: 'sales-milestone',
        type: 'sales',
        priority: 'high',
        title: '売上目標達成！',
        description: `本日の売上: ¥${todayRevenue.toLocaleString()}`,
        timestamp: new Date(),
        actionRequired: false,
      });
    }

    // 活動を時間順でソートして返す
    return activities
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 10);
  };

  /**
   * 利用可能時間枠を計算
   */
  const calculateAvailableSlots = (reservations: Reservation[]): number => {
    const todayReservations = reservations.filter(r => {
      return safeDateComparison(r.date, today) && r.status !== 'cancelled';
    });

    // 営業時間を10:00-20:00と仮定し、1時間枠で計算
    const totalSlots = 10; // 10時間 = 10枠
    const bookedSlots = todayReservations.length;
    
    return Math.max(0, totalSlots - bookedSlots);
  };

  /**
   * チャートデータを生成（過去90日間）
   */
  const generateChartData = (
    reservations: Reservation[],
    treatments: TreatmentRecord[],
    customers: ExtendedCustomer[]
  ): ChartDataPoint[] => {
    const chartDataMap = new Map<string, ChartDataPoint>();
    
    // 過去90日分の日付を生成
    const today = new Date();
    for (let i = 89; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      chartDataMap.set(dateStr, {
        date: dateStr,
        visitors: 0,
        revenue: 0,
        newCustomers: 0,
        repeatCustomers: 0,
      });
    }

    // 完了した予約から来店数を集計
    reservations
      .filter(r => r.status === 'completed')
      .forEach(reservation => {
        const dateStr = reservation.date.toISOString().split('T')[0];
        
        const dataPoint = chartDataMap.get(dateStr);
        if (dataPoint) {
          dataPoint.visitors += 1;
          
          // 顧客の来店回数から新規/リピーターを判定
          const customer = customers.find(c => c.id === reservation.customerId);
          if (customer) {
            if (customer.totalVisits === 1) {
              dataPoint.newCustomers += 1;
            } else if (customer.totalVisits >= 2) {
              dataPoint.repeatCustomers += 1;
            }
          }
        }
      });

    // 施術記録から売上を集計
    treatments.forEach(treatment => {
      const dateStr = treatment.date.toISOString().split('T')[0];
      
      const dataPoint = chartDataMap.get(dateStr);
      if (dataPoint) {
        dataPoint.revenue += treatment.totalAmount || 0;
      }
    });

    // Map → 配列に変換してソート
    return Array.from(chartDataMap.values())
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  /**
   * データを全て取得・更新
   */
  const refreshData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // 全データを並行取得（統一されたDataService使用）
      const [customers, reservations, treatments] = await Promise.all([
        DataService.getCustomers(),
        DataService.getReservations(),
        DataService.getTreatments(),
      ]);

      // 今日の予約を取得（安全な日付比較使用） + 時間順ソート
      const todayReservations = reservations
        .filter(r => {
          return safeDateComparison(r.date, today) && r.status !== 'cancelled';
        })
        .sort((a, b) => {
          // 時間順（HH:MM形式）で昇順ソート
          const timeA = a.startTime || '00:00';
          const timeB = b.startTime || '00:00';
          return timeA.localeCompare(timeB);
        });

      // デバッグ情報の出力（ソート情報含む）
      logger.log('🔍 Dashboard Debug Info (with sort):', {
        todayString: today,
        totalReservations: reservations.length,
        reservationSample: reservations.slice(0, 3).map(r => ({
          id: r.id,
          date: r.date,
          dateType: typeof r.date,
          status: r.status
        })),
        filteredTodayCount: todayReservations.length,
        todayReservationsSorted: todayReservations.slice(0, 5).map((r, index) => ({
          index: index + 1,
          id: r.id,
          customerName: r.customerName,
          startTime: r.startTime,
          date: r.date,
          status: r.status
        })),
        sortVerification: {
          isTimeOrderCorrect: todayReservations.length <= 1 || 
            todayReservations.every((r, i) => i === 0 || 
              (todayReservations[i-1].startTime || '00:00') <= (r.startTime || '00:00')
            )
        }
      });

      // データを計算・更新
      const calculatedMetrics = await calculateMonthlyMetrics(customers, reservations, treatments);
      const frequentCustomersList = getFrequentCustomers(customers);
      const activities = generateRecentActivities(reservations, customers, treatments);
      const availableSlotCount = calculateAvailableSlots(reservations);
      const generatedChartData = generateChartData(reservations, treatments, customers);

      // 状態を更新
      setMetrics(calculatedMetrics);
      setTodaysReservations(todayReservations);
      setFrequentCustomers(frequentCustomersList);
      setRecentActivities(activities);
      setAvailableSlots(availableSlotCount);
      setChartData(generatedChartData);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '予期しないエラーが発生しました';
      setError(errorMessage);
      logger.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [today]);

  // 初回データ取得
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    metrics,
    todaysReservations,
    frequentCustomers,
    recentActivities,
    availableSlots,
    chartData,
    loading,
    error,
    refreshData,
  };
};
